import { Component, computed, effect, ElementRef, HostListener, input, output, signal } from '@angular/core';
import { analogeMeting, cKC_Analog } from '@wasm/KopWeb';
import { KopConfigService } from '../../core/kop-config.service';
import { JSBridgeService } from '../../core/jsbridge.service';
import { AnalogIn, ModBusIn, ModuleAI, MqttIn, RangedKanaal } from '../PropTypes';
import { wasmVectorToArray } from '../../utils/wasmVector';
import { KopModulesService } from '../../modules/kop-modules.service';
import { KanaalService } from '../../kanaal/kanaal.service';
import { aiTypeToKanaalType, ANALOG_DEVICES } from '../../utils/AITypes';
import { MetaService } from '../../core/meta.service';
import { TranslateService } from '../../i18n/translate.service';
import { FocusManagerService } from '../../keynav/focus-manager.service';
import { BaseElementComponent } from '../../keynav/base-element/base-element.component';

@Component({
  selector: 'aieditor',
  templateUrl: './aieditor.component.html',
  styleUrl: './aieditor.component.scss'
})
export class AIEditorComponent extends BaseElementComponent {


  constructor(public kopConfigService: KopConfigService, public jsBridgeService: JSBridgeService,
    public kopModulesService: KopModulesService, public kanaalService: KanaalService, private metaService: MetaService,
    private translate: TranslateService, elementRef: ElementRef<HTMLElement>, focusManager: FocusManagerService) {

    super(elementRef,focusManager);
    this.keyProperty = "ai-editor";
    effect(() => {
      let ai = this.initialValue();
      if (ai != undefined) {
        this.updateEditValue(ai);
        if (this.editOnly() && this.editValue.index > 0) {
          this.valueChanged.emit(this.editValue);
        }
      }
    });
  }

  // public ai = input<cKC_Analog>();
  public initialValue = input<ModuleAI>();


  public isNew = input<boolean>(false);
  public editOnly = input<boolean>(false);
  public autoFill = input<boolean>(false);

  public assignment = computed(() => {
    let meta = this.meta();
    let afd = this.kopConfigService.selectedAfd();
    if (meta != undefined && afd != undefined) {
      return this.kanaalService.buildAssignementInfo('ai', afd?.afdeling(), meta);
    }

  });

  public valueChanged = output<ModuleAI>();
  private _metaIdx = signal<number | undefined>(undefined);

  public metaIdx = input<number | undefined>(undefined);

  private newMetaIdx = computed(() => {
    if (this._metaIdx() != undefined) {
      return this._metaIdx();
    }
    return this.metaIdx();
  });

  public onlyDesc = input<boolean>(false);

  public editValue: ModuleAI = {
    index: 0,
    kanaal: 0,
    range: 0,
    typeIndex: 0
  };

  updateEditValue(v: ModuleAI) {
    this.editValue = v;
    let ai = this.editValue;

    if (ai != undefined) {
      if (ai.kanaal != undefined && ai.typeIndex != undefined) {
        let aiType = this.metaService.aiTypes().find(ai.typeIndex);
        this.kanaalType = aiTypeToKanaalType(aiType);
        this.isVirtual = !AnalogIn.equals(this.kanaalType);
        return;
      }
    }
    this.kanaalType = AnalogIn;
  }

  public kanaalType = AnalogIn;
  public isVirtual = false;

  public kanaal = computed(() => {
    let ai = this.initialValue();
    if (ai != undefined) {
      if (ai.kanaal != undefined && ai.typeIndex != undefined) {
        let aiType = this.metaService.aiTypes().find(ai.typeIndex);
        var typeKanalen = aiType.kanalen;
        let kanaalType = aiTypeToKanaalType(aiType);
        let virtual = !AnalogIn.equals(kanaalType);
        return this.kanaalService.rangedKanaalLabel({
          ioType: kanaalType,
          range: typeKanalen,
          start: virtual ? ai.kanaal >> 1 : ai.kanaal
        });
      }
    } else {
      return "";
    }
  });

  alreadySelectedFN() {
    let tmAIs = this.kopConfigService.selectedAfd()?.afdeling().analogs;
    if (tmAIs != undefined) {
      let ais = wasmVectorToArray(tmAIs);
      let ret = (idx: number) => {
        return ais.find(a => a.index == idx) != undefined;
      }
      return ret;
    }
    return (idx: number) => false;
  }

  labelFn() {
    let ret = (meta: any) => {
      //(meta as Losdef).omschr.toString()
      let m = meta as analogeMeting;
      return this.translate.translateKopData('ai', m.index, m.omschrij?.toString());
    };
    return ret;
  }


  // labelFn() {
  //   let ret = (meta: any) => (meta as analogeMeting).omschrij.toString();
  //   return ret;
  // }

  public aiOptions = computed(() => {
    if (this.isNew()) {
      let aiMetas = this.kopConfigService.currentMeta().ais;
      let tmAIs = this.kopConfigService.selectedAfd()?.afdeling().analogs;
      if (tmAIs != undefined) {
        let selectedModule = this.kopModulesService.selectedAfdModule();
        let ret = aiMetas.filter(aim => {
          if (selectedModule != undefined) {
            if (selectedModule.meta.allAnalogIngagen == undefined) {
              return false;
            }
            if (selectedModule.meta.allAnalogIngagen.indexOf(aim.index) < 0) {
              return false;
            }
          }
          return true;
        });
        return ret.sort((a1, a2) => {
          return a1.omschrij.toString().localeCompare(a2.omschrij.toString());
        });
      }
    }
    return [];
  });


  public typeDesc = computed(() => {
    let ai = this.initialValue();
    if ((ai != undefined) && (ai.typeIndex != undefined)) {
      var ait = this.metaService.aiTypes().find(ai.typeIndex);
      if (ait != undefined) {
        return ait.kort;
      }
    }
    return "";
  });


  public meta = computed(() => {
    let ai = this.initialValue();
    if (ai != undefined) {
      let meta = this.kopConfigService.currentMeta();
      return meta.ais.find(m => m.index == ai.index);
    }
    let chosenMeta = this.newMetaIdx();
    if (chosenMeta != undefined) {
      let meta = this.kopConfigService.currentMeta();
      return meta.ais.find(m => m.index == chosenMeta);
    }
  });

  public aiTypes = computed(() => {
    let meta = this.meta();
    if (meta != undefined) {
      let supportedTypes = this.metaService.aiTypes().findByGroup(meta.typeGroep);
      let ret: { index: number, omschrij: string }[] = [];
      let db = this.translate.kopDataDBSignal();
      for (let i = 0; i < supportedTypes.length; i++) {
        let ait = supportedTypes[i];
        let tmOmshc = this.translate.translateKopDataSync(db, 'aitype', ait.index, ait.omschrij.toString());
        ret.push({
          index: ait.index,
          omschrij: tmOmshc
        });
      }
      // let tmp = combineLatest(ret).pipe(map(res => res));
      return ret;
    }
    return undefined;
  });


  onNew(e: { index: number }) {
    let idx = e.index;
    this._metaIdx.set(idx);
    let ev = this.kopConfigService.aiKCtoModule(idx, undefined);
    this.updateEditValue(ev);
    this.isEditing = true;
  }

  onDelete() {
    let ai = this.initialValue();
    this.kopConfigService.applyAfdUpdate(afd => {
      if ((ai != undefined) && (afd != undefined)) {
        this.kopConfigService.eraseAI(afd, ai);
      }
      return afd;
    });
    this.close();
  }


  onTypeChanged(type: number) {
    let ret = { ... this.editValue };
    let v = this.editValue;
    ret.typeIndex = type;
    var newType = this.metaService.aiTypes().find(type);
    if (newType != undefined) {
      var isA = v.kanaal & 1;
      if (isA && newType.kanalen > 1) {
        ret.kanaal = v.kanaal - 1;
        ret.range = newType.kanalen;
      } else {
        ret.kanaal = v.kanaal;
        ret.range = newType.kanalen;
      }
    }
    this.updateEditValue(ret);
    this.valueChanged.emit(this.editValue);
  }

  onKanaalChange(kanaal: number) {
    let v = this.editValue;
    let ret = { ...v };
    var isA = v.kanaal & 1;
    var newKanaal = kanaal * 2 + isA;
    ret.kanaal = newKanaal;
    this.updateEditValue(ret);
    this.valueChanged.emit(this.editValue);
  }

  onAKanaalChange(isA: boolean | string) {
    let v = this.editValue;
    let ret = { ...v };
    var baseKanaal = v.kanaal >> 1;
    var newKanaal = baseKanaal * 2 + (isA ? 1 : 0);
    ret.kanaal = newKanaal;
    this.updateEditValue(ret);
    this.valueChanged.emit(this.editValue);
  }

  onKanaalInputChanged(event: RangedKanaal) {
    let v = this.editValue;
    let ret = { ...v };
    ret.kanaal = event.start;
    this.updateEditValue(ret);
    this.valueChanged.emit(this.editValue);
  }

  halfValue(a: number) {
    return a >> 1;
  }

  isA(a: number) {
    return (a & 1) == 1;
  }

  onSave() {
    this.close();
    this.kopConfigService.upsertAI(this.editValue);
  }


}
